import React, { useEffect, useState, useMemo } from 'react';

import { observer } from 'mobx-react';
import { toast } from "react-toastify";
import { hexToRgb } from '@helpers/hexToRgb';
import { getStudentIcon } from '@helpers/getStudentIcon';
import {
  getGender,
  getKnowledgeLevel,
  getSpecialNeeds,
  getGermanLanguageLevel,
  getHelpWithStudy,
} from "@helpers/getGermanNaming";

import { getLocalisedErrorMessage } from "@helpers/errorMapper";

import Loader from '@components/Loader';

import kidsApi from "@api/kids.api";
import studentsApi from '@api/students.api';
import classesApi from "@api/classes.api";

import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

import useCurrentProject from '@hooks/useCurrentProject';
import projectsApi from '@api/projects.api';
import * as S from './styles';

const KarteScreen = () => {
  const Map = useMemo(() => new window.SearchChMap({ centerpois: true, marker: false, poigroups: '-', zoom: 8 }), []);

  const { currentProject } = useCurrentProject();
  const [distributionMap, setDistributionMap] = useState([]);
  const [resultsArr, setResults] = useState([]);
  const [editKids, setEditKids] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [teachers, setTeachers] = useState(new Set());
  const [errorsArr, setErrorsArr] = useState([]);
  let arr = [];
  let Poi;

  const fetchDistribution = async () => {
    const filterObj = localStorage.getItem("filters");
    const studentsFilterObj = localStorage.getItem("studentFilters");
    if (!currentProject?._id) return;

    const { _id } = currentProject;
    const { type } = currentProject;
    const DEFAULT_OBJ = () => {
      if (type === 'Nursery') {
        if (filterObj == null) {
          return {
            gender: true,
            distance: true,
            germanLevel: true,
          }
        } else {
          return JSON.parse(filterObj);
        }
      } else {
        if (studentsFilterObj === null) {
          return {
            gender: true,
            knowledgeLevel: true
          }
        } else {
          return JSON.parse(studentsFilterObj);
        }
      }
    }

    setIsLoading(true);

    try {
      const { results, errors } = await projectsApi.getDistribution(_id, type, DEFAULT_OBJ());
      const result: any = {};

      if (currentProject?.type === 'Nursery') {
        results.map((classItem: any) => {
          if (!result[classItem.institution._id]) {
            result[classItem.institution._id] = { ...classItem.institution, kids: [...classItem.kids] };
          } else {
            result[classItem.institution._id].kids = [...result[classItem.institution._id].kids, ...classItem.kids];
          }
        });
      } else {
        results.map((classItem: any) => {
          if (!result[classItem.institution._id]) {
            result[classItem.institution._id] = { ...classItem.institution, students: [...classItem.students] };
          } else {
            result[classItem.institution._id].students = [...result[classItem.institution._id].students, ...classItem.students];
          }
        });
      }

      setDistributionMap(Object.values(result));
      setResults(results);
      errors.length && setErrorsArr(errors);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      return false;
    }
  };

  useEffect(() => {
    if (errorsArr.length) {
      toast.error(getLocalisedErrorMessage(errorsArr[0]))
    }
  }, [errorsArr]);

  useEffect(() => {
    const getTeachers = async () => {
      if (!currentProject?._id) return;
      const classes = await classesApi.fetch(currentProject._id);
      const classTeachers = new Set();
      classes.forEach((e) => classTeachers.add(e.teacherName));
      setTeachers(classTeachers);
    };
    getTeachers();
    fetchDistribution();
  }, [currentProject]);

  useEffect(() => {
    if (distributionMap.length) {
      const getInstitutionColor = (institutionId) => {
        const { colorMapping } = currentProject;

        if (colorMapping && colorMapping[institutionId]) {
          return colorMapping[institutionId];
        } else {
          return "#ffffff"
        }
      };

      let coordinatesObj = {};
      distributionMap.forEach((e, index) => {
        Map.addPOI(new SearchChPOI({
          center: e.location?.coordinates,
          title: `<strong>${e.address}</strong>`,
          icon: `data:image/svg+xml,<svg viewBox="0 0 91 91" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M31.1172 49.6062H49.2624V30.624V22.0368H31.1172V49.6062ZM41.2848 25.3512H46.6032V29.5694H41.2848V25.3512ZM41.2848 33.7877H46.6032V38.006H41.2848V33.7877ZM41.2848 42.2243H46.6032V46.4425H41.2848V42.2243ZM33.9329 25.3512H39.0948V29.5694H33.7764V25.3512H33.9329ZM33.9329 33.7877H39.0948V38.006H33.7764V33.7877H33.9329ZM33.9329 42.2243H39.0948V46.4425H33.7764V42.2243H33.9329Z" fill='${hexToRgb(getInstitutionColor(e._id))}'/>
          <path d="M54.5808 49.6062H61.6199C62.0892 49.6062 62.5584 49.1543 62.5584 48.7023V35.5955H54.5808V49.6062Z" fill='${hexToRgb(getInstitutionColor(e._id))}'/>
          <path d="M45.8211 2C26.1117 2 10 17.5172 10 36.4995C10 52.1673 21.2625 66.0274 36.9049 69.9443L43.944 87.872C44.2568 88.6253 44.8825 89.0772 45.8211 89.0772C46.6032 89.0772 47.3853 88.6253 47.6982 87.872L54.4244 70.095C70.2232 66.3287 81.6421 52.4686 81.6421 36.6501C81.4857 17.5172 65.5305 2 45.8211 2ZM67.8768 48.853C67.8768 52.1673 65.0612 54.7284 61.7763 54.7284H49.4188H25.9552V16.9146H54.5808V30.624H67.8768V48.853Z" fill='${hexToRgb(getInstitutionColor(e._id))}' />
          </svg>`,
          width: 45,
          height: 45,
          html: `
          <div>
            <div style="
              display: flex;
              justify-content: space-between;
              gap: 15px;
              flex-wrap: wrap;
            ">
              <div style="
                flex: 1;
              ">
                <div
                  style="display: flex; gap: 2px; flex-direction: column;"
                >${resultsArr.length &&
            resultsArr.map(c => `
                    <div>${c.institution._id === e._id ?
                `<div style="margin-bottom: 15px">
                        <div><strong>Name Klasse</strong>: <span>${c.name}</span></div>
                        <div><strong>Name Lehrperson</strong>: <span>${c.teacherName}</span></div>
                        <div><strong>Anzahl männlich</strong>: <span>${c.stats.malesCount || 0}</span></div>
                        <div><strong>Anzahl weiblich</strong>: <span>${c.stats.femalesCount || 0}</span></div>
                        <div><strong>Total</strong>: <span>${+c.stats.femalesCount + +c.stats.malesCount}</span></div>
                      </div>` : ''}
                    </div>
                  `).join('')}
                </div>
              </div>
          </div>
        </div>
          `
        }));
        if (currentProject?.type === 'Nursery') {
          if (e.kids.length) {
            e.kids.forEach((k) => {
              if (coordinatesObj[k.location?.coordinates]) {
                coordinatesObj[k.location?.coordinates] += 1;
              } else {
                coordinatesObj[k.location?.coordinates] = 1;
              }
            })

            e.kids.forEach((k) => {
              Poi = new SearchChPOI({
                center: k.location?.coordinates,
                title: `<img src='${getStudentIcon(k, false, getInstitutionColor(e._id))}' alt="" /><strong>${k.fullName}</strong>`,
                icon: coordinatesObj[k.location?.coordinates] === 1 ? getStudentIcon(k, false, getInstitutionColor(e._id)) : getStudentIcon(k, true, getInstitutionColor(e._id)),
                html: `
                  <div>
                    <div style="
                      display: flex;
                      align-items: center;
                      justify-content: space-between;
                      gap: 15px;
                      flex-wrap: wrap;
                      font-size: 15px;
                    ">
                      <div style="
                        flex: 1;
                        min-width: 48%;
                      ">
                        <strong style="
                          margin: 0 0 3px;
                          font-size: 15px;
                        ">Adresse</strong>
                        <p>${k?.address}</p>
                      </div>
  
                      <div style="
                        flex: 1;
                        min-width: 48%;
                        font-size: 15px;
                      ">
                        <strong style="
                          margin: 0 0 3px;
                          font-size: 15px;
                        ">Geschlecht</strong>
                        <p>${getGender(k?.gender)}</p>
                      </div>
  
                      <div style="
                        flex: 1;
                        min-width: 48%;
                        font-size: 14px;
                      ">
                        <strong style="
                          margin: 0 0 3px;
                          font-size: 15px;
                        ">Deutsch</strong>
                        <p>${getGermanLanguageLevel(k?.germanLanguageLevel)}</p>
                      </div>

                      <div style="
                        flex: 1;
                        min-width: 100%;
                        font-size: 14px;
                      ">
                        <strong style="
                          margin: 0 0 3px;
                          font-size: 15px;
                        ">${k?.note ? 'Notizen' : ''}</strong>
                        <p>${k?.note ? k?.note : ''}</p>
                      </div>
                    </div>
                  </div>
                `
              });
              Map.addPOI(Poi);
              Poi.addEventListener("popupclose", function () {
                arr = [];
                setEditKids([]);
              });
              Poi.addEventListener("popupopen", function () {
                setTimeout(() => {
                  arr.push(k);
                  setEditKids(arr);
                }, 400)
              });
            });
          }
        } else {
          if (e.students.length) {
            e.students.forEach((k) => {
              if (coordinatesObj[k.location?.coordinates]) {
                coordinatesObj[k.location?.coordinates] += 1;
              } else {
                coordinatesObj[k.location?.coordinates] = 1;
              }
            })
            e.students.forEach((k) => {
              Poi = new SearchChPOI({
                center: k.location?.coordinates,
                title: `<img src='${getStudentIcon(k, false, getInstitutionColor(e._id))}' alt="" /><strong>${k.fullName}</strong>`,
                icon: coordinatesObj[k.location?.coordinates] === 1 ? getStudentIcon(k, false, getInstitutionColor(e._id)) : getStudentIcon(k, true, getInstitutionColor(e._id), coordinatesObj[k.location?.coordinates]),
                html: `
                  <div>
                    <div style="
                      display: flex;
                      align-items: center;
                      justify-content: space-between;
                      gap: 15px;
                      flex-wrap: wrap;
                      font-size: 15px;
                    ">
                      <div style="
                        flex: 1;
                        min-width: 48%;
                      ">
                        <strong style="
                          margin: 0 0 3px;
                          font-size: 15px;
                        ">Adresse</strong>
                        <p>${k?.address}</p>
                      </div>
  
                      <div style="
                        flex: 1;
                        min-width: 48%;
                        font-size: 15px;
                      ">
                        <strong style="
                          margin: 0 0 3px;
                          font-size: 15px;
                        ">Geschlecht</strong>
                        <p>${getGender(k?.gender)}</p>
                      </div>
                    
                      <div style="
                        flex: 1;
                        min-width: 100%;
                        font-size: 14px;
                      ">
                        <strong style="
                          margin: 0 0 3px;
                          font-size: 15px;
                        ">${k?.note ? 'Bemerkungen' : ''}</strong>
                        <p>${k?.note ? k?.note : ''}</p>
                      </div>
                      
                      <div style="
                        flex: 1;
                        min-width: 100%;
                        font-size: 14px;
                      ">
                        <strong style="
                          margin: 0 0 3px;
                          font-size: 15px;
                        ">${k?.bestFriendName ? 'Freunde' : ''}</strong>
                        <p>${k?.bestFriendName ? k?.bestFriendName : ''}</p>
                      </div>

                      <div style="
                        flex: 1;
                        min-width: 100%;
                        font-size: 14px;
                      ">
                        <strong style="
                          margin: 0 0 3px;
                          font-size: 15px;
                        ">${k?.knowledgeLevel ? 'Anforderungen' : ''}</strong>
                        <p>${k?.knowledgeLevel ? getKnowledgeLevel(k?.knowledgeLevel) : ''}</p>
                      </div>

                      <div style="
                        flex: 1;
                        min-width: 100%;
                        font-size: 14px;
                      ">
                        <strong style="
                          margin: 0 0 3px;
                          font-size: 15px;
                        ">${k?.specialNeeds.length ? 'Fördermassnahmen' : ''}</strong>
                        <p>${k?.specialNeeds.length ? k?.specialNeeds.map((value) => (
                          getSpecialNeeds(value)
                        )) : ''}</p>
                      </div>

                      <div style="
                        flex: 1;
                        min-width: 100%;
                        font-size: 14px;
                      ">
                        <strong style="
                          margin: 0 0 3px;
                          font-size: 15px;
                        ">${k?.helpWithStudy.length ? 'Lernbegleitung SHP' : ''}</strong>
                        <p>${k?.helpWithStudy.length ? k?.helpWithStudy.map((value) => (
                          getHelpWithStudy(value)
                        )) : ''}</p>
                      </div>
                    </div>
                  </div>
                `
              });
              Map.addPOI(Poi);
              Poi.addEventListener("popupclose", function () {
                arr = [];
                setEditKids([]);
              });
              Poi.addEventListener("popupopen", function () {
                setTimeout(() => {
                  arr.push(k);
                  setEditKids(arr);
                }, 400)
              });
            });
          }
        }
      });
    }

  }, [distributionMap]);

  const handleEdit = async ({ target }: { target: HTMLInputElement }, kid: any) => {
    const { name, value, checked } = target;
    if (name === 'schoolBus') {
      await kidsApi.edit(
        {
          ...kid,
          id: kid._id,
          [name]: checked
        });
    } else {
      if (currentProject.type === 'Nursery') {
        await kidsApi.edit(
          {
            ...kid,
            id: kid._id,
            [name]: value
          });
      } else {
        await studentsApi.edit(
          {
            ...kid,
            id: kid._id,
            [name]: value
          });
      }
    }
    setTimeout(() => window.location.reload(false), 400);
  }

  return (
    <>
      <S.Wrap>
        <S.Form style={{}}>
          {editKids.length ? editKids.map((k) => (
            <div style={{ padding: '12px' }}>
              <h3 style={{ marginBottom: 5 }}>{k.fullName}</h3>
              {currentProject?.type === 'Nursery' &&
                <S.FormItem>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="schoolBus"
                        onChange={(e) => handleEdit(e, k)}
                        checked={k?.schoolBus}
                      />
                    }
                    label="SchulBus"
                  />
                </S.FormItem>
              }
              <S.FormItem>
                <InputLabel htmlFor="teacherName" shrink={false}>
                  Lehrperson
                </InputLabel>
                <Select
                  labelId="teacherName"
                  id="teacherName"
                  label="teacherName"
                  name="teacherName"
                  value={k.teacherName}
                  onChange={(e) => handleEdit(e, k)}
                >
                  <MenuItem value=" ">Nein</MenuItem>
                  {teachers.size && [...teachers].map((e) => (
                    <MenuItem value={e}>{e}</MenuItem>
                  ))}
                </Select>
              </S.FormItem>
            </div>
          )) : null}
        </S.Form>
        {isLoading && <Loader filled={true} />}
        <div id="mapcontainer" style={{ width: 'calc(100% + 98px)', height: 'calc(100vh - 90px)', marginTop: '-12px', marginLeft: '-48px' }} />
      </S.Wrap>
    </>
  );
};

export default observer(KarteScreen);
